import {
  type CommittedMapping,
  type CommitMappingsResponse,
  type FailedMapping,
  type MappingToCommit,
  type MatchResult,
  type MatchToSitesResponse,
  type MatchCandidate,
  useAsyncOperationWithResult,
  useDemandData,
  useOrganisationList,
} from "@inrange/building-manager-api-client";
import { reversed, sortBy } from "@inrange/utils";
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import Select from "react-select";
import { HhDataTempFile } from "./DemandFileSummaryRow";
import { customerDataFraction, renderFillCoverageBadge } from "./fillCoverage";

interface Props {
  files: HhDataTempFile[];
  onClose: () => void;
}

type Stage = "pick-org" | "review" | "committing" | "committed";

// Constant siteIds the BE stages these batch LROs under (see match_to_sites.py /
// commit_mappings.py). The poll hook keys /async_status on (siteId, operationKey), so these
// must match the BE constants exactly.
const MATCH_TO_SITES_SITE_ID = "hh-data-match-to-sites";
const COMMIT_MAPPINGS_SITE_ID = "hh-data-commit-mappings";

interface PerRowDecision {
  // Which candidate the operator selected; null = rejected, undefined = no decision yet
  selectedSiteId: string | null | undefined;
  // For multi-esys sites, the chosen esys key (defaults to "0" when there's only one)
  selectedEsys: string;
  // Whether the operator typed in a free-text "other site" override
  overrideSiteId: string | null;
}

type OrgSite = { id: string; name: string; esys: string[] };

// Committing two files onto the same (site, esys) would silently overwrite one with the other
// (the second hhDemandGuid wins), so a (site, esys) target may be claimed by at most one file.
const mappingKey = (siteId: string, esys: string) => `${siteId}::${esys}`;

const esysForSite = (
  siteId: string,
  orgSites: OrgSite[],
  fallback?: string[]
): string[] => orgSites.find((s) => s.id === siteId)?.esys ?? fallback ?? ["0"];

// The (site, esys) target a decision resolves to, or null when the row is rejected/undecided.
const decisionKey = (dec: PerRowDecision | undefined): string | null => {
  const siteId = dec?.overrideSiteId ?? dec?.selectedSiteId;
  if (!siteId) return null;
  return mappingKey(siteId, dec?.selectedEsys ?? "0");
};

// Files whose best candidate is below this confidence default to "Reject - skip this file":
// the matcher isn't confident enough to pre-pick a site, so the operator opts in explicitly.
const DEFAULT_REJECT_BELOW_CONFIDENCE = 50;

// Default-select per row, greedily avoiding (site, esys) collisions. Rows are processed in
// confidence order so the strongest match claims a contested target; a row whose every
// candidate's (site, esys) is already taken is left undecided for the operator to resolve.
const computeDefaultDecisions = (
  matches: MatchResult[],
  orgSites: OrgSite[]
): Record<string, PerRowDecision> => {
  const claimed = new Set<string>();
  const result: Record<string, PerRowDecision> = {};
  const ordered = [...matches].sort(
    (a, b) => (b.candidates[0]?.confidence ?? 0) - (a.candidates[0]?.confidence ?? 0)
  );
  const reject: PerRowDecision = {
    selectedSiteId: null,
    selectedEsys: "0",
    overrideSiteId: null,
  };
  for (const m of ordered) {
    // Low-confidence files default to reject rather than auto-picking a shaky match.
    if ((m.candidates[0]?.confidence ?? 0) < DEFAULT_REJECT_BELOW_CONFIDENCE) {
      result[m.file_guid] = reject;
      continue;
    }
    let decision: PerRowDecision | null = null;
    for (const c of m.candidates) {
      const freeEsys = esysForSite(c.site_id, orgSites, c.esys_keys).find(
        (e) => !claimed.has(mappingKey(c.site_id, e))
      );
      if (freeEsys) {
        decision = {
          selectedSiteId: c.site_id,
          selectedEsys: freeEsys,
          overrideSiteId: null,
        };
        claimed.add(mappingKey(c.site_id, freeEsys));
        break;
      }
    }
    result[m.file_guid] = decision ?? reject;
  }
  return result;
};

/**
 * Bulk-match modal: pick an org, review per-file shortlists, accept/edit/reject,
 * commit the lot. Multi-site files expand into per-curve rows.
 *
 * Backs design doc §7.5-§7.6.
 */
const MatchToSitesModal = ({ files, onClose }: Props) => {
  const { matchToSites, commitMappings } = useDemandData();
  const { fetchOrganisations } = useOrganisationList();

  const [stage, setStage] = useState<Stage>("pick-org");
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgSites, setOrgSites] = useState<
    { id: string; name: string; esys: string[] }[]
  >([]);
  const [matches, setMatches] = useState<MatchResult[]>([]);
  // Candidate-site count for the loading message. The matcher response carries the
  // authoritative org-sites list, but that only arrives after the request - so we seed the
  // count from the picked org's ownerships (already loaded) to avoid showing "0" while loading.
  const [candidateCount, setCandidateCount] = useState<number | null>(null);
  const [decisions, setDecisions] = useState<Record<string, PerRowDecision>>({});
  const [commitResult, setCommitResult] = useState<{
    committed: CommittedMapping[];
    failed: FailedMapping[];
  } | null>(null);

  // Matching + committing are tier-2 LROs: the POST returns an operationKey, then we poll
  // /async_status until the operation result (matches/orgSites, or committed/failed) lands.
  const matchOp = useAsyncOperationWithResult<MatchToSitesResponse>({
    siteId: MATCH_TO_SITES_SITE_ID,
  });
  const commitOp = useAsyncOperationWithResult<CommitMappingsResponse>({
    siteId: COMMIT_MAPPINGS_SITE_ID,
  });

  const { result: matchResult, setResult: setMatchResult } = matchOp;
  const { result: commitResultRaw, setResult: setCommitResultRaw } = commitOp;

  // Consume the match result once polling completes: default-select the top candidate per row
  // and seed the "Other site" typeahead with the full org-sites list the matcher returned.
  useEffect(() => {
    if (!matchResult) return;
    if (matchResult.type === "error") {
      alert(`Matching failed: ${matchResult.message}`);
      setMatchResult(null);
      return;
    }
    const response = matchResult.response;
    if (!response) return;
    setMatches(response.matches);
    const orgSitesArr: OrgSite[] = (response.orgSites || []).map((s) => ({
      id: s.id,
      name: s.name,
      esys: s.esys_keys ?? ["0"],
    }));
    setOrgSites(orgSitesArr);
    // Greedy collision-free default-selection so no two files start out mapped to the same
    // (site, esys) - the per-row UI then prevents the operator re-creating a collision.
    setDecisions(computeDefaultDecisions(response.matches, orgSitesArr));
    setMatchResult(null);
  }, [matchResult, setMatchResult]);

  // Consume the commit result once polling completes.
  useEffect(() => {
    if (!commitResultRaw) return;
    if (commitResultRaw.type === "error") {
      alert(`Commit failed: ${commitResultRaw.message}`);
      setCommitResultRaw(null);
      setStage("review");
      return;
    }
    if (commitResultRaw.response) {
      setCommitResult(commitResultRaw.response);
      setStage("committed");
    }
    setCommitResultRaw(null);
  }, [commitResultRaw, setCommitResultRaw]);

  // Matching spans the kickoff POST, the poll, and the brief window before the result effect
  // consumes the payload - keep the spinner up across all three so the table never flashes empty.
  const matching =
    !!matchOp.operationKey || !!matchResult || matchToSites.isLoading;

  const orgOptions = useMemo(() => {
    const orgs = (fetchOrganisations.data?.organisations ?? []) as Array<{
      id: string;
      name?: string;
    }>;
    return orgs
      .slice()
      .sort(sortBy((o: { name?: string }) => (o.name || "").toLowerCase()))
      .map((o) => ({ label: o.name || "(no name)", value: o.id }));
  }, [fetchOrganisations.data]);

  // The uploaded temp file behind each match row, so the row can show its multi-curve chip +
  // customer-data coverage alongside the matcher's filename.
  const filesByGuid = useMemo(() => {
    const map = new Map<string, HhDataTempFile>();
    for (const f of files) map.set(f.fileGuid, f);
    return map;
  }, [files]);

  const handlePickOrg = async () => {
    if (!orgId) return;
    setStage("review");

    // Seed the candidate count from the picked org's ownerships so the loading text shows a
    // real number rather than 0 (orgSites state isn't populated until the response returns).
    const pickedOrg = (fetchOrganisations.data?.organisations ?? []).find(
      (o: { id: string }) => o.id === orgId
    );
    const distinctSiteIds = new Set(
      (pickedOrg?.siteOwnerships ?? [])
        .map((o: { siteID?: string }) => o.siteID)
        .filter((id): id is string => !!id)
    );
    setCandidateCount(distinctSiteIds.size);

    // Kick off the matcher LRO and start polling; the result is consumed in the effect above.
    const kickoff = await matchToSites.mutateAsync({
      fileGuids: files.map((f) => f.fileGuid),
      orgId,
    });
    matchOp.setOperationKey(kickoff.operationKey);
  };

  // Which file currently claims each (site, esys) target - drives the per-row gating so a
  // target picked on one row can't be picked on another.
  const claimedKeys = useMemo(() => {
    const claims = new Map<string, string>();
    for (const [fileGuid, dec] of Object.entries(decisions)) {
      const key = decisionKey(dec);
      if (key) claims.set(key, fileGuid);
    }
    return claims;
  }, [decisions]);

  // Accept each qualifying row's top candidate, but never onto a (site, esys) another row
  // already holds. Process in confidence order so the strongest match wins a contested target;
  // a qualifying row whose top candidate is fully claimed is left untouched for manual review.
  const bulkAcceptTopCandidates = (minConfidence: number) => {
    const next = { ...decisions };
    const claimed = new Set<string>();
    // Seed with the targets of rows this action won't reassign, so we don't steal them.
    for (const m of matches) {
      const top = m.candidates[0];
      if (!top || top.confidence < minConfidence) {
        const key = decisionKey(next[m.file_guid]);
        if (key) claimed.add(key);
      }
    }
    const ordered = [...matches].sort(
      (a, b) => (b.candidates[0]?.confidence ?? 0) - (a.candidates[0]?.confidence ?? 0)
    );
    for (const m of ordered) {
      const top = m.candidates[0];
      if (!top || top.confidence < minConfidence) continue;
      const freeEsys = esysForSite(top.site_id, orgSites, top.esys_keys).find(
        (e) => !claimed.has(mappingKey(top.site_id, e))
      );
      if (freeEsys) {
        next[m.file_guid] = {
          selectedSiteId: top.site_id,
          selectedEsys: freeEsys,
          overrideSiteId: null,
        };
        claimed.add(mappingKey(top.site_id, freeEsys));
      }
    }
    setDecisions(next);
  };

  const handleBulkAcceptGreen = () => bulkAcceptTopCandidates(85);
  const handleBulkAcceptGreenAndYellow = () => bulkAcceptTopCandidates(60);

  const handleCommit = async () => {
    const mappings: MappingToCommit[] = [];
    for (const m of matches) {
      const dec = decisions[m.file_guid];
      const siteId = dec?.overrideSiteId ?? dec?.selectedSiteId;
      if (!siteId) continue;
      mappings.push({
        fileGuid: m.file_guid,
        siteId,
        esys: dec?.selectedEsys ?? "0",
      });
    }
    if (mappings.length === 0) {
      alert("Nothing selected to commit.");
      return;
    }
    // Defensive backstop: the per-row UI prevents collisions, but never commit two files onto
    // the same (site, esys) - the second would silently overwrite the first.
    const seen = new Set<string>();
    for (const mp of mappings) {
      const key = mappingKey(mp.siteId, mp.esys);
      if (seen.has(key)) {
        alert(
          "Two files are mapped to the same site + energy match. Resolve the duplicate before committing."
        );
        return;
      }
      seen.add(key);
    }
    setStage("committing");
    try {
      // Kick off the commit LRO and start polling; the result is consumed in the effect above.
      const kickoff = await commitMappings.mutateAsync({ mappings });
      commitOp.setOperationKey(kickoff.operationKey);
    } catch (err) {
      alert(`Commit failed: ${err}`);
      setStage("review");
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Match {files.length} file(s) to sites</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {stage === "pick-org" && (
          <Form.Group>
            <Form.Label>Target organisation</Form.Label>
            <Select
              options={orgOptions}
              isLoading={fetchOrganisations.isLoading}
              onChange={(opt) => setOrgId(opt?.value ?? null)}
              placeholder="Choose an org..."
            />
            <div className="mt-3">
              <small className="text-muted">
                The matcher will consider all sites in this org as candidates.
              </small>
            </div>
          </Form.Group>
        )}

        {stage === "review" && (
          <>
            {matching && (
              <div className="text-center my-4">
                <Spinner animation="border" /> Matching {files.length} file(s)
                against {candidateCount ?? "…"} candidate sites...
              </div>
            )}
            {!matching && (
              <>
                <div className="mb-3 d-flex gap-2 justify-content-between align-items-center">
                  <div>
                    Review each suggestion. Click a candidate to switch.
                    Confidence: <Badge bg="success">&ge;85 green</Badge>{" "}
                    <Badge bg="warning">60-84 yellow</Badge>{" "}
                    <Badge bg="danger">&lt;60 red</Badge>
                  </div>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-success" onClick={handleBulkAcceptGreen}>
                      Accept all green
                    </Button>
                    <Button size="sm" variant="outline-warning" onClick={handleBulkAcceptGreenAndYellow}>
                      Accept green + yellow
                    </Button>
                  </div>
                </div>
                <Table responsive bordered size="sm">
                  <thead>
                    <tr>
                      <th style={{ width: "26%" }}>Filename</th>
                      <th style={{ width: "9%" }}>Customer data</th>
                      <th>Candidates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches
                      .slice()
                      .sort(reversed(sortBy((m: MatchResult) => m.candidates[0]?.confidence ?? 0)))
                      .map((m) => (
                        <MatchRow
                          key={m.file_guid}
                          match={m}
                          file={filesByGuid.get(m.file_guid)}
                          decision={decisions[m.file_guid]}
                          onDecisionChange={(d) =>
                            setDecisions({ ...decisions, [m.file_guid]: d })
                          }
                          orgSites={orgSites}
                          claimedKeys={claimedKeys}
                        />
                      ))}
                  </tbody>
                </Table>
              </>
            )}
          </>
        )}

        {stage === "committing" && (
          <div className="text-center my-5">
            <Spinner animation="border" />
            <div className="mt-2">Committing mappings...</div>
          </div>
        )}

        {stage === "committed" && commitResult && (
          <>
            <Alert variant={commitResult.failed.length === 0 ? "success" : "warning"}>
              Committed {commitResult.committed.length} of{" "}
              {commitResult.committed.length + commitResult.failed.length}.
            </Alert>
            {commitResult.failed.length > 0 && (
              <Table size="sm">
                <thead>
                  <tr>
                    <th>File GUID</th>
                    <th>Site ID</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {commitResult.failed.map((f, i) => (
                    <tr key={i}>
                      <td>{f.fileGuid.slice(0, 8)}...</td>
                      <td>{f.siteId.slice(0, 8)}...</td>
                      <td className="text-danger">{f.error}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {stage === "pick-org" && (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!orgId} onClick={handlePickOrg}>
              Next
            </Button>
          </>
        )}
        {stage === "review" && (
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCommit}>
              Commit selected mappings
            </Button>
          </>
        )}
        {stage === "committed" && (
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

interface MatchRowProps {
  match: MatchResult;
  file: HhDataTempFile | undefined;
  decision: PerRowDecision | undefined;
  onDecisionChange: (d: PerRowDecision) => void;
  orgSites: OrgSite[];
  // (site, esys) -> fileGuid that currently claims it, across all rows.
  claimedKeys: Map<string, string>;
}

const MatchRow = ({
  match,
  file,
  decision,
  onDecisionChange,
  orgSites,
  claimedKeys,
}: MatchRowProps) => {
  // A (site, esys) target is blocked for this row if another row already claims it.
  const isBlocked = (siteId: string, esys: string) => {
    const owner = claimedKeys.get(mappingKey(siteId, esys));
    return owner !== undefined && owner !== match.file_guid;
  };
  // The site's first esys not claimed by another row (so picking a site never lands on a taken
  // target); falls back to the first esys when every one is claimed.
  const firstFreeEsys = (siteId: string, fallback?: string[]) => {
    const esysList = esysForSite(siteId, orgSites, fallback);
    return esysList.find((e) => !isBlocked(siteId, e)) ?? esysList[0] ?? "0";
  };
  const siteFullyClaimed = (siteId: string, fallback?: string[]) =>
    esysForSite(siteId, orgSites, fallback).every((e) => isBlocked(siteId, e));

  const otherSiteOptions = useMemo(
    () =>
      orgSites
        .slice()
        .sort(sortBy((s) => s.name.toLowerCase()))
        .map((s) => ({ label: s.name, value: s.id })),
    [orgSites]
  );

  const confidenceBadge = (c: MatchCandidate) => {
    const bg = c.confidence >= 85 ? "success" : c.confidence >= 60 ? "warning" : "danger";
    return <Badge bg={bg}>{c.confidence.toFixed(0)}%</Badge>;
  };

  const selectedSite = decision?.overrideSiteId ?? decision?.selectedSiteId;
  const targetSiteEsys =
    orgSites.find((s) => s.id === selectedSite)?.esys ?? ["0"];

  return (
    <tr>
      <td>
        <div style={{ wordBreak: "break-all" }}>{match.filename}</div>
        <div className="text-muted small">
          {match.source === "explicit" && <Badge bg="info">explicit</Badge>}
          {match.multi_site_suspected && (
            <Badge bg="secondary" className="ms-1">multi-site</Badge>
          )}
          {file?.isSplitChild && (
            <Badge
              bg="info"
              className="ms-1"
              title={
                file.splitLabel
                  ? `Split from a multi-curve file - curve "${file.splitLabel}"`
                  : "One curve split out of a multi-curve file"
              }
            >
              Multi-curve file
            </Badge>
          )}
        </div>
      </td>
      <td>{renderFillCoverageBadge(customerDataFraction(file?.outputMetaData))}</td>
      <td>
        {match.candidates.length === 0 && (
          <div className="text-muted">No candidates found</div>
        )}
        {match.candidates.map((c) => {
          const blocked = siteFullyClaimed(c.site_id, c.esys_keys);
          return (
            <Form.Check
              type="radio"
              key={c.site_id}
              id={`${match.file_guid}-${c.site_id}`}
              name={`row-${match.file_guid}`}
              disabled={blocked}
              checked={
                decision?.overrideSiteId == null && decision?.selectedSiteId === c.site_id
              }
              onChange={() =>
                onDecisionChange({
                  selectedSiteId: c.site_id,
                  // Land on the first esys not already taken by another row; multi-esys
                  // candidates surface the picker below for the operator to refine.
                  selectedEsys: firstFreeEsys(c.site_id, c.esys_keys),
                  overrideSiteId: null,
                })
              }
              label={
                <span className={blocked ? "text-muted" : ""}>
                  {c.site_name} {confidenceBadge(c)}{" "}
                  {match.source === "explicit" && (
                    <Badge bg="info" className="ms-1">
                      RFI
                    </Badge>
                  )}{" "}
                  {c.esys_keys && c.esys_keys.length > 1 && (
                    <Badge bg="secondary" className="ms-1">
                      {c.esys_keys.length} esys
                    </Badge>
                  )}
                  {blocked && (
                    <Badge bg="light" text="dark" className="ms-1">
                      already mapped to another file
                    </Badge>
                  )}
                  <div className="text-muted small">Why: {c.rationale}</div>
                </span>
              }
            />
          );
        })}
        <Form.Check
          type="radio"
          name={`row-${match.file_guid}`}
          id={`${match.file_guid}-reject`}
          checked={decision?.selectedSiteId === null && decision?.overrideSiteId == null}
          onChange={() =>
            onDecisionChange({
              selectedSiteId: null,
              selectedEsys: decision?.selectedEsys ?? "0",
              overrideSiteId: null,
            })
          }
          label={<span className="text-muted">Reject - skip this file</span>}
        />
        <div className="mt-1 d-flex align-items-center gap-2">
          <span className="small text-muted">Or pick another site:</span>
          <div style={{ flex: 1, maxWidth: 360 }}>
            <Select
              isClearable
              placeholder="Search org sites..."
              options={otherSiteOptions}
              isOptionDisabled={(opt) => siteFullyClaimed(opt.value)}
              value={
                decision?.overrideSiteId
                  ? {
                      value: decision.overrideSiteId,
                      label:
                        orgSites.find((s) => s.id === decision.overrideSiteId)
                          ?.name ?? decision.overrideSiteId,
                    }
                  : null
              }
              onChange={(opt) =>
                onDecisionChange({
                  selectedSiteId: decision?.selectedSiteId ?? null,
                  // Pick the first free esys of the chosen site so the override never lands on
                  // a (site, esys) another row already holds.
                  selectedEsys: opt ? firstFreeEsys(opt.value) : decision?.selectedEsys ?? "0",
                  overrideSiteId: opt?.value ?? null,
                })
              }
            />
          </div>
        </div>
        {selectedSite && targetSiteEsys.length > 1 && (
          <div className="mt-1">
            <span className="small text-muted me-2">Esys:</span>
            {targetSiteEsys.map((e) => {
              const esysBlocked = !!selectedSite && isBlocked(selectedSite, e);
              return (
                <Form.Check
                  inline
                  type="radio"
                  key={e}
                  name={`esys-${match.file_guid}`}
                  id={`${match.file_guid}-esys-${e}`}
                  disabled={esysBlocked}
                  checked={decision?.selectedEsys === e}
                  onChange={() =>
                    onDecisionChange({
                      selectedSiteId: decision?.selectedSiteId ?? null,
                      selectedEsys: e,
                      overrideSiteId: decision?.overrideSiteId ?? null,
                    })
                  }
                  label={esysBlocked ? `esys ${e} (taken)` : `esys ${e}`}
                />
              );
            })}
          </div>
        )}
      </td>
    </tr>
  );
};

export default MatchToSitesModal;
